import React, { useEffect, useState } from "react";
import { Container, CircularProgress } from "@mui/material";
import { Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { savePlaylistPosition } from "../../reducers/myDesigns/myDesignsThunks";
import { myDesignService } from "../../services";
import DndKit from "../dragNDrop/DndKit";
import PlaylistElement from "../dragNDrop/draggableElements/PlaylistElement";

const PREFIX = "PlaylistContentManager";

const classes = {
  playlistContainer: `${PREFIX}-playlistContainer`,
  loaderContainer: `${PREFIX}-loaderContainer`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.playlistContainer}`]: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    height: "100%",
    [theme.breakpoints.up("sm")]: {
      minWidth: "350px",
    },
  },

  [`&.${classes.loaderContainer}`]: {
    position: "relative",
    left: "50%",
    transform: "translateX(-50%)",
    width: "20%",
    marginTop: "2rem",
  },
}));

export default function PlaylistContentManager(props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const [items, setItems] = useState(null);

  const fetchingLoading = useSelector(
    (state) => state.myDesigns.selectedZone.fetchingLoading
  );
  const role = useSelector((state) => state.user?.currentUser?.account?.role);
  const [hasAccess] = useState(
    role === "ADMIN" || props.currentZone.editContentAccess === 1
  );

  useEffect(() => {
    if (props.playlist) {
      setItems(props.playlist.map((elem) => ({ ...elem, editing: false })));
    }
  }, [props.playlist]);

  const saveMyPlaylistPosition = async (
    zoneId,
    elementId,
    newPosition,
    finalItems,
    items
  ) => {
    try {
      const thunk = await dispatch(
        savePlaylistPosition({ zoneId, elementId, newPosition, finalItems })
      );
      if (thunk.meta.requestStatus === "rejected") throw new Error("offline");
    } catch {
      setItems(items.map((elem) => ({ ...elem, editing: false })));
      enqueueSnackbar(t("toasts.getInfosError"), { variant: "error" });
    }
  };

  const handleItemModification = (id, key, value, item = null) => {
    if (key === "editing" && !value) {
      myDesignService
        .updatePlaylistItemInfos(props.zoneId, id, item)
        .then(() => {
          enqueueSnackbar(t("toasts.contentInfosModifySuccess"), {
            variant: "success",
          });
        })
        .catch((err) => {
          console.log("err =>", err);
          if (err === 403)
            enqueueSnackbar(t("toasts.unauthorizedError"), {
              variant: "error",
            });
          else
            enqueueSnackbar(t("toasts.contentInfosModifyError"), {
              variant: "error",
            });
        });
    }
    const tmp = [...items];
    const myIndex = tmp.findIndex((elem) => elem.id === id);
    if (myIndex >= 0) tmp[myIndex] = { ...tmp[myIndex], [key]: value };
    setItems(tmp);
  };

  const handleSaveOrderedArray = (newArr, elemId, newIndex) => {
    setItems(newArr);
    saveMyPlaylistPosition(props.zoneId, elemId, newIndex, newArr, items);
  };

  if (fetchingLoading || !props.playlist || !items) {
    return (
      <Container>
        <Root>
          <div className={classes.loaderContainer}>
            <CircularProgress size={50} />
          </div>
        </Root>
      </Container>
    );
  } else {
    return (
      <Root sx={{ height: "100%" }}>
        <div className={classes.playlistContainer}>
          {props.playlist.length === 0 ? (
            <Typography variant="body1" className={classes.marginTop}>
              {t("myDesign.zones.playlists.emptyPlaylist")}
            </Typography>
          ) : (
            <DndKit
              mode={"vertical"}
              getObjectMovedInfos
              data={items}
              reorderedArray={handleSaveOrderedArray}
              customWidth
              keyToDisable={"editing"}
              renderItem={(elem) => (
                <PlaylistElement
                  id={elem.id}
                  title={elem.title || props.zoneLabel}
                  description={elem.description}
                  type={elem.type}
                  hasAccess={hasAccess}
                  isEditing={elem.editing}
                  handleItemModification={handleItemModification}
                  onRemove={() =>
                    props.addOrRemoveMyElement(
                      props.currentZone.id,
                      elem.id,
                      false,
                      elem
                    )
                  }
                  onPreview={() =>
                    props.setMedia({
                      content: elem.targetUrl ? elem.targetUrl : elem.directUrl,
                      title: elem.title,
                      type: elem.type,
                      description: elem.description,
                      full: elem,
                    })
                  }
                />
              )}
            />
          )}
        </div>
      </Root>
    );
  }
}
